import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AuthShell } from "@charphq/ui/components/auth";
import { Button } from "@charphq/ui/components/base/button";
import { Checkbox } from "@charphq/ui/components/base/checkbox";
import { Alert, AlertDescription } from "@charphq/ui/components/base/alert";
import { Loader2, Check } from "lucide-react";
import { authClient } from "../lib/auth-client";

/**
 * OAuth 2.1 consent screen. The authorization server (backend, better-auth
 * oauth-provider plugin) redirects here with the signed authorize query
 * (`client_id`, `scope`, `sig`, …). Approving POSTs /oauth2/consent — the auth
 * client forwards the signed `oauth_query` automatically — and follows the
 * returned redirect back to the requesting app (e.g. an MCP client).
 *
 * Scopes use the app's `resource:action` vocabulary (see mcp/permissions.ts).
 * They are grouped for readability, and a single toggle lets the user grant a
 * read-only token by dropping the "Manage" (write) scopes from the accepted set.
 */

const OIDC_SCOPES = new Set(["openid", "profile", "email", "offline_access"]);

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  openid: "Confirm your identity",
  profile: "Read your name and profile picture",
  email: "Read your email address",
  offline_access: "Keep access without asking again (refresh tokens)",
  "customer:read": "Read customers",
  "project:read": "Read projects",
  "device:read": "Read devices, health and data gaps",
  "deployment:read": "Read deployments",
  "event:read": "Read device timelines",
  "device:update": "Schedule and cancel device replays",
  "event:create": "Add device timeline events",
};

const isReadScope = (s: string) => s.endsWith(":read");
const isOpsScope = (s: string) => !OIDC_SCOPES.has(s);
const isWriteScope = (s: string) => isOpsScope(s) && !isReadScope(s);

function useOAuthQuery() {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  return {
    clientId: params.get("client_id"),
    scopes: (params.get("scope") ?? "").split(" ").filter(Boolean),
  };
}

function ScopeList({ scopes }: { scopes: string[] }) {
  return (
    <ul className="mt-2 space-y-2">
      {scopes.map((scope) => (
        <li key={scope} className="flex items-start gap-2 text-sm text-muted-foreground">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-status-ok" />
          <span>
            {SCOPE_DESCRIPTIONS[scope] ?? scope}
            <span className="ml-1 text-xs text-muted-foreground/70">({scope})</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export const Route = createFileRoute("/oauth-consent")({
  component: function OAuthConsent() {
    const { clientId, scopes } = useOAuthQuery();
    const [clientName, setClientName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<"accept" | "deny" | null>(null);
    const [allowWrites, setAllowWrites] = useState(true);

    const requested = scopes.length ? scopes : ["openid"];
    const signInScopes = useMemo(() => requested.filter((s) => OIDC_SCOPES.has(s)), [requested]);
    const readScopes = useMemo(() => requested.filter(isReadScope), [requested]);
    const writeScopes = useMemo(() => requested.filter(isWriteScope), [requested]);

    useEffect(() => {
      if (!clientId) return;
      let cancelled = false;
      authClient
        .$fetch("/oauth2/public-client", { query: { client_id: clientId } })
        .then((res: { data?: { name?: string } | null }) => {
          if (!cancelled) setClientName(res.data?.name ?? null);
        })
        .catch(() => {
          // Client metadata is cosmetic — consent still works without it.
        });
      return () => {
        cancelled = true;
      };
    }, [clientId]);

    const decide = async (accept: boolean) => {
      setSubmitting(accept ? "accept" : "deny");
      setError(null);
      try {
        // Only send an explicit `scope` when restricting (dropping writes); omitting it
        // accepts the originally-requested set.
        const restrict = accept && writeScopes.length > 0 && !allowWrites;
        const body: { accept: boolean; scope?: string } = { accept };
        if (restrict) {
          body.scope = requested.filter((s) => !isWriteScope(s)).join(" ");
        }
        const res = await authClient.$fetch("/oauth2/consent", { method: "POST", body });
        const data = res.data as { redirect?: boolean; url?: string } | null;
        if (data?.redirect && data.url) {
          window.location.href = data.url;
          return;
        }
        setError("Unexpected response from the authorization server.");
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Consent failed. Please retry from the client app.",
        );
      } finally {
        setSubmitting(null);
      }
    };

    if (!clientId) {
      return (
        <AuthShell
          title="Authorization request"
          description="This page is only reachable from an application requesting access."
        >
          <Alert variant="destructive">
            <AlertDescription>
              Missing OAuth parameters — start the connection again from the requesting application.
            </AlertDescription>
          </Alert>
        </AuthShell>
      );
    }

    return (
      <AuthShell
        title="Authorize access"
        description={`${clientName ?? "An application"} wants to access your CHARP Operations account`}
      >
        <div className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {signInScopes.length > 0 && (
            <div>
              <p className="font-display text-sm font-medium text-foreground">Sign-in</p>
              <ScopeList scopes={signInScopes} />
            </div>
          )}

          {readScopes.length > 0 && (
            <div>
              <p className="font-display text-sm font-medium text-foreground">
                Read operations data
              </p>
              <ScopeList scopes={readScopes} />
            </div>
          )}

          {writeScopes.length > 0 && (
            <div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="allowWrites"
                  checked={allowWrites}
                  onCheckedChange={(v) => setAllowWrites(v === true)}
                  disabled={submitting !== null}
                />
                <label
                  htmlFor="allowWrites"
                  className="font-display text-sm font-medium text-foreground"
                >
                  Also allow performing actions
                </label>
              </div>
              <div className={allowWrites ? undefined : "opacity-40"}>
                <ScopeList scopes={writeScopes} />
              </div>
              {!allowWrites && (
                <p className="mt-1 text-xs text-muted-foreground">
                  A read-only token will be issued — the app can view data but not make changes.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              disabled={submitting !== null}
              onClick={() => decide(false)}
            >
              {submitting === "deny" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Deny
            </Button>
            <Button className="flex-1" disabled={submitting !== null} onClick={() => decide(true)}>
              {submitting === "accept" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Allow access
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  },
});
